import * as React from "react";
import { useEffect, useMemo } from "react";
import styled from "@mui/material/styles/styled";
import { DataInputPanelEdit } from "../../DataInputPanelEdit";
import { PlanPanel, useBuildPlanPanels } from "../useBuildPlanPanels";
import { useSuggestionsForPlan } from "../hooks/useSuggestionsForPlan";
import { useParams } from "react-router";
import { ThreeDotsLoader } from "fe-web-components/loader/threeDotsLoader";
import { useChangePatientCase } from "../editPatient/useChangePatientCase";
import { PatientCaseGrouping } from "../../../context/diagnosticAgent";
import { DiseaseCategoryType } from "fe-common/graphql";
import { DataInputMobileSidePadding } from "../common/styles/consts";
import { BackButton } from "../common/backButton";
import { useRouteHelper } from "../useRouteHelper";
import { AgentRoute } from "../util/agentRoutes";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { scrollableStyle } from "fe-common/ui/commonStyles";
import { contentZIndex } from "fe-common/ui/stacking";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { DRUG_OR_MEDICAMENT, KahunPanelSubGroup } from "shared";
import { useDaoContext } from "fe-web-components/diseaseview/DaoContext";

//region [[ Styles ]]

const EditPanelPageView = styled("div")(({ theme }) => ({
  [theme.breakpoints.down(MobileView)]: {
    ...scrollableStyle,
    position: "absolute",
    top: "0",
    left: "0",
    backgroundColor: "white",

    margin: "0",
    width: "100%",
    height: "100%",
    zIndex: contentZIndex,
  },
}));

const PanelTitle = styled("div")(({ theme }) => ({
  fontWeight: 600,

  fontSize: "20px",
}));

const TopSection = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor: KahunColorMap.AccentLight,
  paddingTop: "10px",
  paddingLeft: `${DataInputMobileSidePadding}px`,
  paddingRight: `${DataInputMobileSidePadding}px`,
  paddingBottom: "10px",
}));

const DataInputSection = styled("div")(({ theme }) => ({
  paddingBottom: "130px",
  paddingTop: "10px",
  paddingLeft: `${DataInputMobileSidePadding}px`,
  paddingRight: `${DataInputMobileSidePadding}px`,
}));

const BackButtonStyled = styled(BackButton)(({ theme }) => ({
  ".back-button": {
    padding: "0px !important",
    marginTop: "10px",
  },
  marginLeft: "-15px",
  marginRight: "10px",
}));

//endregion [[ Styles ]]

//region [[ Props ]]

export interface EditPanelPageProps {}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export enum PlanTabsEnum {
  IMAGING = "imaging",
  LABS = "labs",
}

let drugFeature;

export const EditPanelPage = ({ ...props }: EditPanelPageProps) => {
  const { panelId } = useParams<{ panelId: string }>();
  const { getCaseValue, setCaseValue } = useChangePatientCase();
  const { navigateTo } = useRouteHelper();
  const dao = useDaoContext();

  let {
    findingSuggestions: suggestions,
    planIsLoading,
    clusteredSuggestions,
  } = useSuggestionsForPlan();

  const {
    labSuggestionsPanels: imagingPanels,
    loading: imagingPanelsLoading,
  } = useBuildPlanPanels(
    suggestions,
    clusteredSuggestions,
    DiseaseCategoryType.Imaging,
  );

  const {
    labSuggestionsPanels: labsPanels,
    loading: labPanelsLoading,
  } = useBuildPlanPanels(
    suggestions,
    clusteredSuggestions,
    DiseaseCategoryType.OutcomeTest,
  );

  const clusteredPanels = useMemo(() => [...imagingPanels, ...labsPanels], [
    imagingPanels,
    labsPanels,
  ]);

  const panel: PlanPanel = clusteredPanels
    .map((c) => c.items)
    .flat()
    .find((p) => p.panel.id === panelId);

  const loading = planIsLoading || labPanelsLoading || imagingPanelsLoading;

  const onBack = () => {
    const planTab =
      panelContext.panelCategory.id === KahunPanelSubGroup.Procedures
        ? PlanTabsEnum.IMAGING
        : PlanTabsEnum.LABS;

    navigateTo({
      route: AgentRoute.RecommendedPlan + "/" + planTab,
    });
  };

  const panelContext = {
    panel: panel ? panel.panel : null,
    caseGrouping: PatientCaseGrouping.WORKUP,
    panelCategory: panel ? panel.panel.panelSubgroup : null,
  };

  const panelSuggestions = useMemo(
    () =>
      panel && panel.suggestions
        ? panel.suggestions.map((s) => ({
            ...s,
            finding: { ...s.finding, qualifiers: [] },
          }))
        : [],
    [panel],
  );

  // Get the drugQf
  useEffect(() => {
    async function fetchData() {
      const drugQf = await dao.getQualifiedFeatureForId(DRUG_OR_MEDICAMENT);
      drugFeature = drugQf;
    }
    if (drugFeature) return;
    fetchData();
  }, [dao]);

  return (
    <EditPanelPageView>
      {loading && <ThreeDotsLoader />}
      {!loading && panel && (
        <>
          <TopSection>
            <BackButtonStyled
              className={"back-button"}
              onClick={onBack}
              color={"black"}
            />
            <PanelTitle>{panel.panel.title}</PanelTitle>
          </TopSection>
          <DataInputSection>
            <DataInputPanelEdit
              suggestions={panelSuggestions}
              getCaseValue={getCaseValue}
              setCaseValue={setCaseValue}
              panelContext={panelContext}
              drugFeature={drugFeature}
            />
          </DataInputSection>
        </>
      )}
    </EditPanelPageView>
  );
};
